import { Comment } from '../../../../comments/builders/comment_builder';
import { IFeedbackPossibleDataSource, TFeedbackDataSourceEntry } from './../builders/plan_feedback_transaction_type';
import { APPlanFeedbackDataSourceUpdateAfterAcceptDeclineStrategy } from './../strategies/plan_feedback_transaction_types_query_strategy';

import { includes } from 'lodash4';
import * as _4 from 'lodash4';
import { StrategySafe } from 'utility-ts-strategy';
import { slotType } from '../../../../global/builders/ap_generic_slot_builder';
import {
  IPlanFeedbackCourse,
  IPlanFeedbackCourseMeta,
  IPlanFeedbackCourseProgramSlot,
  PlanFeedbackCourse
} from '../builders/plan_feedback_course_builder';
import {
  IPlanFeedbackCourseGroup,
  IPlanFeedbackCourseGroupMeta,
  PlanFeedbackCourseGroup
} from '../builders/plan_feedback_course_group_builder';
import {
  IPlanFeedbackGenericSlotBuilder,
  PlanFeedbackGenericSlotBuilder
} from '../builders/plan_feedback_generic_slot_builder';
import { PlanFeedbackProgramSlot } from '../builders/plan_feedback_program_slot_builder';
import { IFeedbackTransaction } from '../builders/plan_feedback_transaction_builder';
import { PlanFeedbackTransactionBuilder } from '../builders/plan_feedback_transaction_builder';
import {
  IPlanFeedbackSuggestion,
  PlanFeedbackGenericSlotType,
  planFeedbackTransactionActionType,
  planFeedbackTransactionType
} from '../builders/plan_feedback_transaction_type';
import { IPlanFeebackCourseGroupsDatasource } from '../datasources/plan_feedback_course_groups_datasource';
import { IPlanFeebackCoursesDatasource } from '../datasources/plan_feedback_courses_datasource';
import { IPlanFeedbackProgramSlotsDatasource } from '../datasources/plan_feedback_program_slots_datasource';
import { feedbackSuggestedPlanStatus, IFeedbackSuggestedPlanModel } from '../models/feedback_suggested_plan_model';
import { ApPlanSuggestionTransactionTextStrategy } from '../strategies/plan_feedback_transaction_text';
import {
  PlanFeedbackSuggestionsForTransactionTypeConditionalStrategy,
  PlanFeedbackTermFieldIdentifierStrategy
} from '../strategies/plan_feedback_transaction_types_query_strategy';

interface IApiParamsForAcceptReject {
  nk: string;
  action: planFeedbackTransactionActionType;
}

type ICategorizedSuggestion = {
  [key in planFeedbackTransactionType]: {
    [termId: number]: IPlanFeedbackSuggestion[];
  };
};

export type FlaggedPlanFeedbackCourse = PlanFeedbackCourse &
  IPlanFeedbackCourseMeta & {
    flagInfo?: Comment;
  };

export type FlaggedPlanFeedbackCourseGroup = PlanFeedbackCourseGroup &
  IPlanFeedbackCourseGroupMeta & {
    flagInfo?: Comment;
  };

export const TemplateChangesTransactionTypeText = {
  MET: 'Marked as Met',
  UNMET: 'Marked as Unmet',
  NONE: 'Comment Updates'
};

export class PlanFeedbackServant {
  static checkPlanStatus(plan: { status: feedbackSuggestedPlanStatus }, status: feedbackSuggestedPlanStatus) {
    return _4.get(plan, 'status') === status;
  }

  static isPlanCommitedAndBlocked(plan: IFeedbackSuggestedPlanModel) {
    const planStatus = { status: plan.status };
    const isStillInEditing = plan.from_editing;
    return PlanFeedbackServant.isPlanBlocked(planStatus) && !isStillInEditing;
  }

  static isPlanBlocked(plan: { status: feedbackSuggestedPlanStatus }) {
    return PlanFeedbackServant.checkPlanStatus(plan, 'BLOCKED');
  }

  static isPlanEditing(plan: { status: feedbackSuggestedPlanStatus }) {
    return PlanFeedbackServant.checkPlanStatus(plan, 'EDITING');
  }

  static isPlanResolved(plan: { status: feedbackSuggestedPlanStatus }) {
    return PlanFeedbackServant.checkPlanStatus(plan, 'RESOLVED');
  }

  static isPlanResolving(plan: { status: feedbackSuggestedPlanStatus }) {
    return PlanFeedbackServant.checkPlanStatus(plan, 'RESOLVING');
  }

  static isPlanCommitted(plan: { status: feedbackSuggestedPlanStatus }) {
    return PlanFeedbackServant.checkPlanStatus(plan, 'COMMITTED');
  }

  static isPlanNotCreated(plan: { status: feedbackSuggestedPlanStatus }) {
    return PlanFeedbackServant.checkPlanStatus(plan, '');
  }

  static isPlanReadOnlyMode(plan: { status: feedbackSuggestedPlanStatus }) {
    return PlanFeedbackServant.isPlanNotCreated(plan) || PlanFeedbackServant.isPlanResolved(plan);
  }

  static isPlanSentToStudent(plan: { status: feedbackSuggestedPlanStatus }) {
    return PlanFeedbackServant.isPlanCommitted(plan) || PlanFeedbackServant.isPlanResolving(plan);
  }

  static updateDataSourcesAfterAcceptDecline(
    dataSource: IFeedbackPossibleDataSource,
    action: planFeedbackTransactionActionType,
    resolutions: Array<IFeedbackTransaction['resolution']>
  ) {
    const coursesToUpdate = dataSource.query((planFeedbackCourse) =>
      _4.includes(resolutions, planFeedbackCourse.resolution)
    );
    coursesToUpdate.forEach((courseToDelete) => {
      const suggestion = dataSource.get(courseToDelete.id);
      APPlanFeedbackDataSourceUpdateAfterAcceptDeclineStrategy[action](suggestion, dataSource);
    });
  }

  static isValidProgramSlotFeedback(
    feedbackSuggestion: TFeedbackDataSourceEntry
  ): feedbackSuggestion is PlanFeedbackProgramSlot {
    return _4.includes(['UNMET', 'MET'], feedbackSuggestion.diff);
  }

  static isValidFeedback(feedbackSuggestion: TFeedbackDataSourceEntry) {
    if (PlanFeedbackServant.isValidProgramSlotFeedback(feedbackSuggestion)) {
      return true;
    }
    return (
      _4.includes(['REMOVE', 'ADD', 'MOVE'], feedbackSuggestion.diff) ||
      feedbackSuggestion.lock_modified ||
      feedbackSuggestion.from_comment !== feedbackSuggestion.comment
    );
  }

  static checkFeedbackProgramSlotDiffStatus(
    planFeedbackProgramSlot?: PlanFeedbackProgramSlot,
    diff?: PlanFeedbackProgramSlot['diff']
  ) {
    if (!planFeedbackProgramSlot) {
      return false;
    }
    return planFeedbackProgramSlot.diff === diff;
  }

  static isFeedbackProgramSlotMet(planFeedbackProgramSlot?: PlanFeedbackProgramSlot) {
    return PlanFeedbackServant.checkFeedbackProgramSlotDiffStatus(planFeedbackProgramSlot, 'MET');
  }

  static isFeedbackProgramSlotUnmet(planFeedbackProgramSlot?: PlanFeedbackProgramSlot) {
    return PlanFeedbackServant.checkFeedbackProgramSlotDiffStatus(planFeedbackProgramSlot, 'UNMET');
  }

  static forcedAcceptFeedbackCourse(feedbackCourse: FlaggedPlanFeedbackCourse) {
    return (
      !!feedbackCourse.lock_modified ||
      PlanFeedbackServant.isFeedbackProgramSlotMet(feedbackCourse._feedbackProgramSlot) ||
      PlanFeedbackServant.isFeedbackProgramSlotUnmet(feedbackCourse._feedbackProgramSlot)
    );
  }

  static getTextForFeedbackProgramSlot(planFeedbackProgramSlot: PlanFeedbackProgramSlot) {
    const programSlotObj = planFeedbackProgramSlot.program_slot_obj;
    if (!_4.isEmpty(programSlotObj.desc)) {
      return programSlotObj.desc;
    }

    const strategyForText = new StrategySafe<
      (programSlotObj: PlanFeedbackProgramSlot['program_slot_obj']) => string,
      slotType
    >({
      NCR: () => programSlotObj.course_group.nk,
      COURSE: (prgmSlot: IPlanFeedbackCourseProgramSlot) =>
        `${_4.first(prgmSlot.courses).cd} ${_4.first(prgmSlot.courses).title} `,
      GROUP: () => 'Requirement', // programSlotObj.course_group.nk,
      WILDCARD: () => programSlotObj.course_group.nk
    });

    return strategyForText[programSlotObj.course_group.slot_type](programSlotObj);
  }

  static forcedAcceptFeedbackCourseGroup(feedbackCourseGroup: FlaggedPlanFeedbackCourseGroup) {
    return (
      PlanFeedbackServant.isFeedbackProgramSlotMet(feedbackCourseGroup._feedbackProgramSlot) ||
      PlanFeedbackServant.isFeedbackProgramSlotUnmet(feedbackCourseGroup._feedbackProgramSlot)
    );
  }

  static getSuggestionsForCourses(feedbackCourses: FlaggedPlanFeedbackCourse[]): IFeedbackTransaction[] {
    const longTransaction = true;
    return _4.map(feedbackCourses, (feedbackCourse) =>
      PlanFeedbackTransactionBuilder.build(
        feedbackCourse.nk,
        ApPlanSuggestionTransactionTextStrategy['COURSE'](feedbackCourse),
        ApPlanSuggestionTransactionTextStrategy['COURSE'](feedbackCourse, longTransaction),
        feedbackCourse.diff,
        'COURSE',
        PlanFeedbackServant.forcedAcceptFeedbackCourse(feedbackCourse),
        feedbackCourse.resolution,
        feedbackCourse.course.nk,
        feedbackCourse.from_comment,
        feedbackCourse.comment
      )
    );
  }

  static getTemplateChangesGroupedByTransactionType(planFeedbackProgramSlots: PlanFeedbackProgramSlot[]) {
    return planFeedbackProgramSlots.reduce(
      (groupedSuggestions, suggestion) => {
        if (_4.isEmpty(groupedSuggestions[suggestion.diff])) {
          groupedSuggestions[suggestion.diff] = {
            statusGroup: TemplateChangesTransactionTypeText[suggestion.diff],
            suggestions: []
          };
        }
        groupedSuggestions[suggestion.diff].suggestions.push(PlanFeedbackGenericSlotBuilder.build(suggestion));
        return groupedSuggestions;
      },
      {} as Record<
        IFeedbackTransaction['transactionType'],
        {
          statusGroup: string;
          suggestions: IPlanFeedbackGenericSlotBuilder[];
        }
      >
    );
  }

  static getSuggestionsForProgramSlots(planFeedbackProgramSlots: PlanFeedbackProgramSlot[]): IFeedbackTransaction[] {
    const studentForcedToAccept = true;
    const strategyKey = 'PROGRAM_SLOT';
    return _4.map(planFeedbackProgramSlots, (planFeedbackProgramSlot) =>
      PlanFeedbackTransactionBuilder.build(
        planFeedbackProgramSlot.nk,
        ApPlanSuggestionTransactionTextStrategy[strategyKey](planFeedbackProgramSlot),
        ApPlanSuggestionTransactionTextStrategy[strategyKey](planFeedbackProgramSlot),
        planFeedbackProgramSlot.diff,
        strategyKey,
        studentForcedToAccept, // program slot
        planFeedbackProgramSlot.resolution,
        planFeedbackProgramSlot.program_slot_obj.nk,
        '',
        planFeedbackProgramSlot.comment
      )
    );
  }

  static getSuggestionsForCourseGroups(feedbackCourseGroups: FlaggedPlanFeedbackCourseGroup[]): IFeedbackTransaction[] {
    const longTransaction = true;
    return _4.map(feedbackCourseGroups, (feedbackCourseGroup) =>
      PlanFeedbackTransactionBuilder.build(
        feedbackCourseGroup.nk,
        ApPlanSuggestionTransactionTextStrategy['COURSE_GROUP'](feedbackCourseGroup),
        ApPlanSuggestionTransactionTextStrategy['COURSE_GROUP'](feedbackCourseGroup, longTransaction),
        feedbackCourseGroup.diff,
        'COURSE_GROUP',
        PlanFeedbackServant.forcedAcceptFeedbackCourseGroup(feedbackCourseGroup),
        feedbackCourseGroup.resolution,
        feedbackCourseGroup.course_group.nk
      )
    );
  }

  static checkSuggestionStatus(suggestion: IPlanFeedbackSuggestion, diff: planFeedbackTransactionType) {
    return suggestion.diff === diff;
  }

  static isRemoveSuggestion(suggestion: IPlanFeedbackSuggestion) {
    return PlanFeedbackServant.checkSuggestionStatus(suggestion, 'REMOVE');
  }

  static isAddSuggestion(suggestion: IPlanFeedbackSuggestion) {
    return PlanFeedbackServant.checkSuggestionStatus(suggestion, 'ADD');
  }

  static isNoneSuggestion(suggestion: IPlanFeedbackSuggestion) {
    return PlanFeedbackServant.checkSuggestionStatus(suggestion, 'NONE');
  }

  static isMoveSuggesiton(suggestion: IPlanFeedbackSuggestion) {
    return PlanFeedbackServant.checkSuggestionStatus(suggestion, 'MOVE');
  }

  static getCategorizedAndGroupedByTermSuggestions(suggestions: IPlanFeedbackSuggestion[]): ICategorizedSuggestion {
    return suggestions.reduce((categorizedGroupedByTermSuggestions: ICategorizedSuggestion, suggestion) => {
      const _diffType = suggestion.diff;

      /**
       * Based on the transaction type this function will return suggestions as an array
       * if it is a MOVE, this function will return an array of two suggestions.
       * Why? We need to put the course tile in two term tile (one is where it got removed and one is where it get added)
       * if it is a ADD, we will have only one term to put, so it will return only one object in the array
       * for rest read the function defn
       */
      const suggestionsForDiffType =
        PlanFeedbackSuggestionsForTransactionTypeConditionalStrategy[_diffType](suggestion);

      _4.forEach(suggestionsForDiffType, (_suggestionWithDiffType: any) => {
        const diffType = _suggestionWithDiffType.diff;
        const belongingTermField = PlanFeedbackTermFieldIdentifierStrategy[diffType].currentTermField;
        const suggestionBelongingTerm = suggestion[belongingTermField] as number;

        if (!categorizedGroupedByTermSuggestions[diffType]) {
          categorizedGroupedByTermSuggestions[diffType] = {};
        }

        if (!categorizedGroupedByTermSuggestions[diffType][suggestionBelongingTerm]) {
          categorizedGroupedByTermSuggestions[diffType][suggestionBelongingTerm] = [];
        }
        categorizedGroupedByTermSuggestions[diffType][suggestionBelongingTerm].push(_suggestionWithDiffType);
      });
      return categorizedGroupedByTermSuggestions;
    }, {} as ICategorizedSuggestion);
  }

  static formApiParamsForAcceptReject(
    feedbacks: IFeedbackTransaction[],
    action: planFeedbackTransactionActionType,
    type: PlanFeedbackGenericSlotType
  ) {
    return feedbacks.reduce((apiParams: IApiParamsForAcceptReject[], feedback) => {
      if (feedback['type'] === type) {
        apiParams.push({
          nk: feedback['nk'],
          action
        });
      }
      return apiParams;
    }, []);
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/suggestions/components/plan_feedback/servants/plan_feedback_servant.ts